import { useEffect } from 'react'
import { Loader2, MessageSquare, ThumbsDown, ThumbsUp } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useSubmitSeriesEpisodeFeedback } from '../../api/series'
import type { SeriesEpisode, SeriesEpisodeFeedback } from '../../types/series'
import Dialog from '../ui/dialog'
import Textarea from '../ui/textarea'
import { useToast } from '../ui/toast-provider'

interface SeriesFeedbackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  seriesId: string
  episode: SeriesEpisode | null
  existingFeedback?: SeriesEpisodeFeedback | null
  onSubmitted?: () => void
}

interface FeedbackFormValues {
  thumbsUp: boolean | null
  comment: string
}

export function SeriesFeedbackDialog({
  open,
  onOpenChange,
  seriesId,
  episode,
  existingFeedback,
  onSubmitted,
}: SeriesFeedbackDialogProps) {
  const submitFeedback = useSubmitSeriesEpisodeFeedback()
  const { addToast } = useToast()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FeedbackFormValues>({
    defaultValues: {
      thumbsUp: existingFeedback?.thumbsUp ?? null,
      comment: existingFeedback?.comment ?? '',
    },
  })

  useEffect(() => {
    reset({
      thumbsUp: existingFeedback?.thumbsUp ?? null,
      comment: existingFeedback?.comment ?? '',
    })
  }, [existingFeedback, episode?.id, reset])

  const thumbsUp = watch('thumbsUp')
  const comment = watch('comment') ?? ''
  const commentLength = comment.length
  const isBusy = isSubmitting || submitFeedback.isPending

  const selectThumbs = (value: boolean) => {
    clearErrors('root')
    setValue('thumbsUp', thumbsUp === value ? null : value, { shouldDirty: true })
  }

  const onSubmit = async (values: FeedbackFormValues) => {
    if (!episode) return

    const trimmedComment = values.comment.trim()
    if (values.thumbsUp === null && !trimmedComment) {
      setError('root', {
        type: 'manual',
        message: 'Add a thumbs response or a short note.',
      })
      return
    }

    try {
      await submitFeedback.mutateAsync({
        id: seriesId,
        podcastId: episode.id,
        thumbsUp: values.thumbsUp,
        comment: trimmedComment || undefined,
      })

      addToast({
        title: existingFeedback ? 'Feedback updated' : 'Feedback saved',
        description: 'We’ll use this to improve the next episodes in this series.',
        variant: 'success',
      })

      onSubmitted?.()
      onOpenChange(false)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not save your feedback. Please try again.'
      addToast({
        title: 'Could not save feedback',
        description: message,
        variant: 'error',
      })
    }
  }

  const footer = (
    <div className="flex items-center justify-end gap-3 px-4 py-4 sm:px-6">
      <button
        type="button"
        onClick={() => onOpenChange(false)}
        disabled={isBusy}
        className="cursor-pointer rounded-full border border-black/10 bg-white px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Not now
      </button>
      <button
        type="submit"
        form="series-feedback-form"
        disabled={isBusy}
        className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-secondary-300"
      >
        {isBusy && <Loader2 className="h-4 w-4 animate-spin" />}
        {existingFeedback ? 'Update feedback' : 'Save feedback'}
      </button>
    </div>
  )

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="How was this episode?"
      size="sm"
      heightMode="auto"
      footer={footer}
    >
      <form id="series-feedback-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <input type="hidden" {...register('thumbsUp')} />

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => selectThumbs(true)}
            className={`group flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border p-5 text-center transition-all ${
              thumbsUp === true
                ? 'border-brand-500 bg-brand-50 shadow-sm ring-1 ring-brand-500'
                : 'border-black/10 bg-white hover:border-black/20 hover:bg-black/[0.02] hover:shadow-sm'
            }`}
          >
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-full border transition-colors ${
                thumbsUp === true
                  ? 'border-brand-200 bg-brand-100 text-brand-700'
                  : 'border-black/5 bg-secondary-50 text-foreground/60 group-hover:bg-white group-hover:text-foreground/80'
              }`}
            >
              <ThumbsUp className="h-6 w-6" />
            </div>
            <div className={`text-sm font-semibold ${thumbsUp === true ? 'text-brand-900' : 'text-foreground'}`}>
              More like this
            </div>
          </button>

          <button
            type="button"
            onClick={() => selectThumbs(false)}
            className={`group flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border p-5 text-center transition-all ${
              thumbsUp === false
                ? 'border-danger-500 bg-danger-50 shadow-sm ring-1 ring-danger-500'
                : 'border-black/10 bg-white hover:border-black/20 hover:bg-black/[0.02] hover:shadow-sm'
            }`}
          >
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-full border transition-colors ${
                thumbsUp === false
                  ? 'border-danger-200 bg-danger-100 text-danger-700'
                  : 'border-black/5 bg-secondary-50 text-foreground/60 group-hover:bg-white group-hover:text-foreground/80'
              }`}
            >
              <ThumbsDown className="h-6 w-6" />
            </div>
            <div className={`text-sm font-semibold ${thumbsUp === false ? 'text-danger-900' : 'text-foreground'}`}>
              Needs work
            </div>
          </button>
        </div>

        <div className="space-y-3">
          <label htmlFor="series-feedback-comment" className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <MessageSquare className="h-4 w-4 text-foreground/50" />
            Any other thoughts?
          </label>
          <Textarea
            id="series-feedback-comment"
            rows={4}
            maxLength={2000}
            placeholder="Tell us what you liked or what felt off..."
            className="min-h-[100px] resize-none rounded-2xl border-black/10 p-4 text-sm focus:border-brand-500 focus:ring-brand-500 shadow-sm"
            {...register('comment', { maxLength: 2000 })}
          />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs">
            <span className="text-foreground/50">Optional, but every bit helps us improve.</span>
            <span className={`font-medium ${commentLength > 1900 ? 'text-warning-600' : 'text-foreground/40'}`}>
              {commentLength}/2000
            </span>
          </div>
        </div>

        {errors.root?.message && (
          <p className="text-sm text-danger-600">{errors.root.message}</p>
        )}
      </form>
    </Dialog>
  )
}

export default SeriesFeedbackDialog